/**
 * RoutingFileUpload Component
 * Handles file uploads for the routing SaaS app (tenant-aware)
 */
import React, { useState, useRef } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { RoutingDataService } from '../services/RoutingDataService';

export default function RoutingFileUpload({
  tenantId,
  fileType,
  compact = false,
  onUploadComplete,
  onUploadError
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [lastUpload, setLastUpload] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (file) => {
    if (!file) return;

    console.log(`Uploading ${fileType} for tenant ${tenantId}:`, file.name);

    try {
      setUploading(true);
      setUploadProgress(0);
      setUploadStage('Preparing upload...');

      // Initialize service with tenant
      await RoutingDataService.initialize();
      RoutingDataService.setTenant(tenantId);

      setUploadStage('Uploading to cloud...');
      setUploadProgress(30);

      // Upload file
      const result = await RoutingDataService.uploadAndProcessFile(file, fileType);

      setUploadProgress(70);
      setUploadStage('Processing...');

      // Wait a moment for processing
      await new Promise(resolve => setTimeout(resolve, 1000));

      setUploadProgress(100);
      setUploadStage('Complete!');
      setLastUpload({ fileName: file.name, timestamp: new Date() });

      console.log(`Upload completed for ${fileType}:`, result);
      onUploadComplete?.(result);

      // Reset after success
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
        setUploadStage('');
      }, 2000);

    } catch (error) {
      console.error(`Upload failed for ${fileType}:`, error);
      setUploadStage(`Error: ${error.message}`);
      onUploadError?.(error);

      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
        setUploadStage('');
      }, 3000);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    if (file) handleFileSelect(file);
  };

  // Compact mode for inline upload buttons
  if (compact) {
    return (
      <div className="relative">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleInputChange}
          className="hidden"
        />

        {uploading ? (
          <div className="flex items-center gap-2 text-xs text-blue-600">
            <Loader2 size={14} className="animate-spin" />
            <span>{uploadStage || `${uploadProgress}%`}</span>
          </div>
        ) : (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors"
          >
            <Upload size={12} />
            Upload
          </button>
        )}

        {lastUpload && !uploading && (
          <div className="text-[10px] text-slate-400 mt-1">
            Last: {lastUpload.fileName}
          </div>
        )}
      </div>
    );
  }

  // Full mode with drag-drop zone
  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className={`
        relative border-2 border-dashed rounded-lg p-4 transition-colors
        ${dragOver ? 'border-blue-400 bg-blue-50' : 'border-slate-300 bg-slate-50'}
        ${uploading ? 'pointer-events-none opacity-75' : 'cursor-pointer hover:border-blue-300'}
      `}
      onClick={() => !uploading && fileInputRef.current?.click()}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        onChange={handleInputChange}
        className="hidden"
      />

      {uploading ? (
        <div className="text-center">
          <Loader2 size={24} className="mx-auto mb-2 text-blue-500 animate-spin" />
          <p className="text-sm text-blue-600">{uploadStage}</p>
          <div className="mt-2 h-1 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      ) : (
        <div className="text-center">
          <Upload size={24} className="mx-auto mb-2 text-slate-400" />
          <p className="text-sm text-slate-600">
            Drop file here or <span className="text-blue-600">browse</span>
          </p>
          <p className="text-xs text-slate-400 mt-1">CSV, Excel files</p>
        </div>
      )}

      {lastUpload && !uploading && (
        <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-slate-500 flex items-center gap-1">
          <CheckCircle size={12} className="text-green-500" />
          Last upload: {lastUpload.fileName}
        </div>
      )}
    </div>
  );
}
